import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  Divider,
  IndexTable,
  InlineGrid,
  InlineStack,
  Page,
  Text,
  useIndexResourceState,
} from '@shopify/polaris';
import type { BadgeProps } from '@shopify/polaris';
import { useApi } from '../hooks/useApi';
import type { Template, TemplateStatus } from '../types';

function statusTone(s: TemplateStatus): BadgeProps['tone'] {
  if (s === 'ACTIVE') return 'success';
  if (s === 'DRAFT') return 'attention';
  return undefined; // ARCHIVED
}

const noProducts = (t: Template) => (t._count?.productLinks ?? 0) === 0;
// Manual templates can be sent without an order ("Send a document"), so they need no products.
const needsProducts = (t: Template) => noProducts(t) && t.triggerType !== 'MANUAL';

function triggerLabel(t: string) {
  if (t === 'ORDER_CREATED') return 'Order created';
  if (t === 'ORDER_PAID') return 'Order paid';
  return 'Manual';
}

// Ready-made contracts from the template library (slugs served by /api/templates/library).
const STARTERS = [
  { slug: 'liability-waiver', name: 'Liability Waiver', hint: 'Activities, rentals, classes' },
  { slug: 'service-agreement', name: 'Service Agreement', hint: 'Services and bookings' },
  { slug: 'rental-agreement', name: 'Rental Agreement', hint: 'Equipment and item rentals' },
  { slug: 'custom-order-approval', name: 'Custom Order Approval', hint: 'Custom and made-to-order products' },
];

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <InlineStack gap="300" blockAlign="start" wrap={false}>
      <div
        style={{
          flexShrink: 0, width: 28, height: 28, borderRadius: '50%', background: '#303030',
          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 600, fontSize: 13,
        }}
      >
        {n}
      </div>
      <BlockStack gap="200">
        <Text as="h3" variant="headingSm">{title}</Text>
        {children}
      </BlockStack>
    </InlineStack>
  );
}

/** Shown on an empty Templates page: the three steps to the first signed contract. */
function GetStartedGuide({
  onLibrary,
  onUpload,
}: {
  onLibrary: (slug: string, name: string) => void;
  onUpload: () => void;
}) {
  return (
    <Card>
      <BlockStack gap="500">
        <BlockStack gap="100">
          <Text as="h2" variant="headingLg">Get started in 3 steps</Text>
          <Text as="p" tone="subdued">
            Set up your first contract and customers will receive a signing link automatically when they order.
          </Text>
        </BlockStack>

        <Step n={1} title="Create a contract">
          <Text as="p" tone="subdued">Start from a ready-made template — you can edit every word:</Text>
          <InlineGrid columns={{ xs: 1, sm: 2 }} gap="200">
            {STARTERS.map((s) => (
              <Button key={s.slug} onClick={() => onLibrary(s.slug, s.name)} textAlign="start" fullWidth>
                {`${s.name} — ${s.hint}`}
              </Button>
            ))}
          </InlineGrid>
          <InlineStack>
            <Button variant="plain" onClick={onUpload}>Or upload your own PDF contract</Button>
          </InlineStack>
        </Step>

        <Divider />

        <Step n={2} title="Link your products">
          <Text as="p" tone="subdued">
            Choose which products need a signature. SignShield sends the contract only for orders that
            contain a linked product — back on this page, click <strong>Link products</strong> next to
            your template.
          </Text>
        </Step>

        <Divider />

        <Step n={3} title="Activate">
          <Text as="p" tone="subdued">
            Click <strong>Activate</strong>. From then on, customers get a signing link by email when their
            order is paid, and you get the signed PDF. You can change when it's sent in the template's
            Settings tab.
          </Text>
        </Step>
      </BlockStack>
    </Card>
  );
}

export default function TemplatesPage() {
  const { request } = useApi();
  const navigate = useNavigate();

  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await request<Template[]>('/templates');
      setTemplates(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id: string) => {
    setNotice(null);
    try {
      const result = await request<{ deleted: boolean; archived: boolean; message?: string }>(
        `/templates/${id}`, { method: 'DELETE' },
      );
      if (result.archived) {
        // Template has sessions — mark it archived in local state instead of removing it
        setTemplates((prev) =>
          prev.map((t) => (t.id === id ? { ...t, status: 'ARCHIVED' as Template['status'] } : t)),
        );
        setNotice(result.message ?? 'Template archived.');
      } else {
        setTemplates((prev) => prev.filter((t) => t.id !== id));
      }
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleActivate = async (id: string) => {
    try {
      const updated = await request<Template>(`/templates/${id}/activate`, { method: 'POST' });
      setTemplates((prev) => prev.map((t) => (t.id === id ? { ...t, ...updated } : t)));
    } catch (e: any) {
      setError(e.message);
    }
  };

  const unlinkedActive = templates.filter((t) => t.status === 'ACTIVE' && needsProducts(t));

  const rowMarkup = templates.map((template, index) => (
    <IndexTable.Row id={template.id} key={template.id} position={index}>
      <IndexTable.Cell>
        <Text variant="bodyMd" fontWeight="semibold" as="span">
          {template.name}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Badge tone={statusTone(template.status)}>
          {template.status.charAt(0) + template.status.slice(1).toLowerCase()}
        </Badge>
      </IndexTable.Cell>
      <IndexTable.Cell>{triggerLabel(template.triggerType)}</IndexTable.Cell>
      <IndexTable.Cell>{template._count?.fields ?? '—'}</IndexTable.Cell>
      <IndexTable.Cell>
        {needsProducts(template) && template.status !== 'ARCHIVED'
          ? <Badge tone="warning">None linked</Badge>
          : noProducts(template) ? '—' : template._count?.productLinks ?? '—'}
      </IndexTable.Cell>
      <IndexTable.Cell>
        <InlineStack gap="200">
          <Button size="slim" onClick={() => navigate(`/templates/${template.id}/edit`)}>
            Edit
          </Button>
          {template.status === 'DRAFT' && (needsProducts(template) ? (
            <Button size="slim" onClick={() => navigate(`/templates/${template.id}/edit?tab=products`)}>
              Link products
            </Button>
          ) : (
            <Button size="slim" tone="success" onClick={() => handleActivate(template.id)}>
              Activate
            </Button>
          ))}
          <Button
            size="slim"
            tone="critical"
            onClick={() => handleDelete(template.id)}
          >
            Delete
          </Button>
        </InlineStack>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <Page
      title="Templates"
      primaryAction={{
        content: 'New template',
        onAction: () => navigate('/templates/new'),
      }}
    >
      <BlockStack gap="400">
        {error  && <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>}
        {notice && (
          <Banner tone="warning" onDismiss={() => setNotice(null)}>
            {notice}
          </Banner>
        )}
        {unlinkedActive.length > 0 && (
          <Banner
            tone="warning"
            title={`${unlinkedActive.length === 1 ? 'An active template has' : `${unlinkedActive.length} active templates have`} no linked products`}
            action={{
              content: 'Link products',
              onAction: () => navigate(`/templates/${unlinkedActive[0].id}/edit?tab=products`),
            }}
          >
            <p>
              SignShield only sends a contract for orders that contain a linked product, so{' '}
              {unlinkedActive.map((t) => `“${t.name}”`).join(', ')} will never be sent until you link products.
            </p>
          </Banner>
        )}

        {!loading && templates.length === 0 ? (
          <GetStartedGuide
            onLibrary={(slug, name) =>
              navigate(`/templates/editor/new?${new URLSearchParams({ slug, name, trigger: 'ORDER_PAID' })}`)
            }
            onUpload={() => navigate('/templates/new')}
          />
        ) : (
          <IndexTable
            resourceName={{ singular: 'template', plural: 'templates' }}
            itemCount={templates.length}
            headings={[
              { title: 'Name' },
              { title: 'Status' },
              { title: 'Trigger' },
              { title: 'Fields' },
              { title: 'Products' },
              { title: 'Actions' },
            ]}
            selectable={false}
            loading={loading}
          >
            {rowMarkup}
          </IndexTable>
        )}
      </BlockStack>
    </Page>
  );
}
